import { useState, useEffect } from 'react';
import { userService } from '@/services/user';
import { UserListItem, GetUsersParams } from '@/types/user';
import { PaginatedResponse } from '@/types/common';
import { useToast } from '@/hooks/use-toast';

const buildUsersFilterKey = (params?: GetUsersParams): string =>
  [
    params?.offset ?? '',
    params?.limit ?? '',
    params?.q ?? '',
    params?.type ?? '',
    params?.role ?? '',
    params?.status ?? '',
    params?.sort_by ?? '',
    params?.order ?? '',
    params?.roles?.join(',') ?? '',
    params?.partner_ids?.join(',') ?? '',
  ].join('|');

export const useUsers = (params?: GetUsersParams) => {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const filterKey = buildUsersFilterKey(params);

  const fetchUsers = async (fetchParams?: GetUsersParams) => {
    try {
      setIsLoading(true);
      setError(null);
      const response: PaginatedResponse<UserListItem> = await userService.getUsers(
        fetchParams ?? params
      );
      setUsers(response.data || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao carregar usuários';
      setError(errorMessage);
      toast({
        variant: 'error',
        title: 'Erro ao carregar usuários',
        description: errorMessage,
      });
      console.error('Error fetching users:', err);
      setUsers([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchUsers(params);
  }, [filterKey]);

  const updateUserStatus = (userId: string, status: UserListItem['status']) => {
    setUsers((previousUsers) =>
      previousUsers.map((user) => (user.id === userId ? { ...user, status } : user))
    );
  };

  return {
    users,
    total,
    isLoading,
    error,
    updateUserStatus,
    refetch: () => fetchUsers(params),
  };
};
